// src/components/WaitlistView.tsx
import React, { useMemo, useState } from 'react';
import type { UserProfile } from '../types';

type WaitlistViewProps = {
  userProfile: UserProfile;
  inviteError?: string | null;
  onSignOut?: () => void | Promise<void>;
  onGoHome?: () => void;
  onRefreshAccess?: () => void | Promise<void>;
};

function cleanErrorMessage(raw?: string | null): string | null {
  const v = String(raw || '').trim();
  if (!v) return null;

  const lower = v.toLowerCase();
  if (lower.includes('invite_required')) {
    return 'Access is still refreshing. Tap Check access, or sign out and sign back in if it does not continue.';
  }
  if (lower.includes('invalid_invite')) return 'That access request could not be verified. Please sign out and use an approved account.';
  if (lower.includes('timeout') || lower.includes('confirm access')) return 'Roddo could not confirm access yet. Check your connection and try again.';
  if (lower.includes('paused') || lower.includes('blocked')) return 'New access is currently paused.';
  if (lower.includes('access is not active')) return v;

  return v;
}

export default function WaitlistView({
  userProfile,
  inviteError = null,
  onSignOut,
  onGoHome,
  onRefreshAccess,
}: WaitlistViewProps) {
  const [busyAction, setBusyAction] = useState<'signout' | 'refresh' | null>(null);

  const n = Number((userProfile as any)?.waitlistNumber);
  const hasSpot = Number.isFinite(n) && n > 0;

  const displayName =
    String(
      (userProfile as any)?.displayName ||
        (userProfile as any)?.username ||
        (userProfile as any)?.name ||
        'angler',
    ).trim() || 'angler';

  const friendlyError = useMemo(() => cleanErrorMessage(inviteError), [inviteError]);
  const isRecoveryState = !hasSpot && !!friendlyError;

  const handleSignOut = async () => {
    if (!onSignOut || busyAction) return;
    try {
      setBusyAction('signout');
      await onSignOut();
    } finally {
      setBusyAction(null);
    }
  };

  const handleRefresh = async () => {
    if (!onRefreshAccess || busyAction) return;
    try {
      setBusyAction('refresh');
      await onRefreshAccess();
    } finally {
      setBusyAction(null);
    }
  };

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
      return;
    }

    if (typeof window !== 'undefined') {
      window.location.assign('/');
    }
  };

  return (
    <main className="min-h-[var(--roddo-vh)] w-full overflow-y-auto bg-[#F7F2E8] dark:bg-[#070B15] px-4 py-[calc(env(safe-area-inset-top)+1.25rem)] text-black dark:bg-[#070B15] dark:text-white">
      <div className="mx-auto flex min-h-[calc(var(--roddo-vh)-2.5rem)] w-full max-w-md items-center justify-center">
        <section className="relative w-full overflow-hidden rounded-[20px] border border-black/10 bg-white/90 p-5 shadow-[0_10px_26px_rgba(89,65,37,0.10)]  dark:border-white/15 dark:bg-[#101827] dark:shadow-[0_6px_16px_rgba(0,0,0,0.16)]">

          <div className="relative flex items-start justify-between gap-3">
            <div>
              <p className="inline-flex rounded-full border border-black/10 bg-amber-100 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-amber-900 shadow-[0_2px_0_rgba(0,0,0,0.12)] dark:border-white/10 dark:bg-white/10 dark:text-amber-100">
                Roddo Account Access
              </p>
              <h1 className="mt-3 text-3xl font-black leading-none tracking-tight text-black dark:text-white">
                {isRecoveryState ? 'Finishing account setup' : 'Account access paused'}
              </h1>
            </div>

            <button
              type="button"
              onClick={handleGoHome}
              className="shrink-0 rounded-[12px] border border-black/10 bg-white px-3 py-2 text-xs font-black text-black shadow-[0_4px_12px_rgba(89,65,37,0.06)] transition hover:-translate-y-0.5 active:translate-y-px dark:border-white/15 dark:bg-white/10 dark:text-white dark:shadow-none"
              aria-label="Go to Roddo home"
            >
              Home
            </button>
          </div>

          <p className="relative mt-4 text-sm font-semibold leading-relaxed text-black/70 dark:text-slate-200">
            {isRecoveryState
              ? `Hey ${displayName}, Roddo found your account and is finishing activation now.`
              : `Hey ${displayName}, new account access is currently paused by Roddo administration.`}
          </p>

          <div className="relative mt-5 rounded-[16px] border border-black/10 bg-[#fff4c7] p-4 shadow-[0_6px_16px_rgba(89,65,37,0.07)] dark:border-white/15 dark:bg-white/10 dark:shadow-none">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.16em] text-black/55 dark:text-slate-300">
                  {isRecoveryState ? 'Access status' : 'Your spot'}
                </div>
                <div className="mt-1 text-4xl font-black text-black dark:text-white">
                  {isRecoveryState ? 'Review' : hasSpot ? `#${n}` : 'Pending'}
                </div>
              </div>
              <div className="grid h-16 w-16 place-items-center rounded-[16px] border border-black/10 bg-white text-3xl shadow-[0_6px_16px_rgba(89,65,37,0.07)] dark:border-white/15 dark:bg-slate-900">
                {isRecoveryState ? '🔐' : '🎣'}
              </div>
            </div>
          </div>

          {friendlyError ? (
            <div className="relative mt-4 rounded-[14px] border border-rose-300 bg-rose-50 p-3 text-sm font-bold leading-relaxed text-rose-800 dark:border-rose-300/25 dark:bg-rose-950/40 dark:text-rose-100">
              {friendlyError}
            </div>
          ) : null}

          <div className="relative mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {onRefreshAccess ? (
              <button
                type="button"
                onClick={handleRefresh}
                disabled={!!busyAction}
                className="rounded-[14px] border border-black bg-white px-4 py-3 text-sm font-black text-black shadow-[0_6px_16px_rgba(89,65,37,0.07)] transition hover:-translate-y-0.5 hover:bg-violet-50 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/15 dark:bg-white/10 dark:text-white dark:shadow-none dark:hover:bg-white/15"
              >
                {busyAction === 'refresh' ? 'Checking…' : 'Check access'}
              </button>
            ) : null}

            {onSignOut ? (
              <button
                type="button"
                onClick={handleSignOut}
                disabled={!!busyAction}
                className="rounded-[14px] border border-black bg-[#2F80ED] dark:bg-[#8A70F2] px-4 py-3 text-sm font-black text-white shadow-[0_8px_18px_rgba(124,58,237,0.18)] transition hover:-translate-y-0.5 hover:brightness-105 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/15"
              >
                {busyAction === 'signout' ? 'Signing out…' : 'Sign out'}
              </button>
            ) : null}
          </div>

          <p className="relative mt-4 text-xs font-semibold leading-relaxed text-black/55 dark:text-slate-300">
            Roddo is in early testing. If access does not refresh automatically, tap Check access or sign out and sign back in.
          </p>
        </section>
      </div>
    </main>
  );
}
